import { uniStaffRegisterApplicant } from '@/store/apply/actions';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BaseRegistrationFormSchema, IUniStaffRegistration, UniStaffFormKeyNames, UniStaffRegistrationSchema } from 'api-paths-cas';
import { z } from 'zod';
import { structureProgrammeChoicesObject } from '../../utils/programmeChoicesHelper';
import { initialUniStaffFormValues, UseApplicationFormReturnType } from './types';
import { useUniStaffUniversities } from '@/utilities/hooks/useUniStaffUniversities';

// Extend the base schema but make programmeChoices optional and add UI fields
// We omit programmeChoices from the base schema and add it back as optional
const UniRegistrationFormSchema = UniStaffRegistrationSchema.omit({
  programmeChoices: true,
}).extend(BaseRegistrationFormSchema);

export type IUniStaffRegistrationForm = z.infer<typeof UniRegistrationFormSchema>;

export function useUniForm(isOpen: boolean): UseApplicationFormReturnType<IUniStaffRegistrationForm> {
  const dispatch = useDispatch();

  const form = useForm<IUniStaffRegistrationForm>({
    resolver: zodResolver(UniRegistrationFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: initialUniStaffFormValues,
  });

  const { handleSubmit, reset, setValue } = form;

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const submitUniOrAdminRegistration = (values: IUniStaffRegistrationForm) => {
    const programmeChoices = structureProgrammeChoicesObject({
      courseId: values[UniStaffFormKeyNames.COURSE_ID],
      courseName: values[UniStaffFormKeyNames.COURSE_NAME],
      intakeId: values[UniStaffFormKeyNames.INTAKE_ID],
      studyLevel: values[UniStaffFormKeyNames.STUDY_LEVEL],
      mode: values[UniStaffFormKeyNames.MODE],
      campus: values[UniStaffFormKeyNames.CAMPUS],
      entryPoint: values[UniStaffFormKeyNames.ENTRY_POINT],
    });

    const registrationData: IUniStaffRegistration = {
      email: values[UniStaffFormKeyNames.EMAIL]?.toLowerCase().trim(),
      preferredName: values[UniStaffFormKeyNames.PREFERRED_NAME],
      universityId: values[UniStaffFormKeyNames.UNIVERSITY_ID],
      nationality: values[UniStaffFormKeyNames.NATIONALITY],
      dob: values[UniStaffFormKeyNames.DOB],
      phoneNumber: values[UniStaffFormKeyNames.MOBILE_NUMBER],
      feeStatus: values[UniStaffFormKeyNames.FEE_STATUS],
      disableEmailNotifications: values[UniStaffFormKeyNames.DISABLE_EMAIL_NOTIFICATIONS],
      programmeChoices,
    };

    dispatch(uniStaffRegisterApplicant(registrationData));
  };

  const onSubmit = (values: IUniStaffRegistrationForm) => {
    submitUniOrAdminRegistration(values);
  };

  const clearDependentFields = useCallback(
    (fields: string[]) => {
      for (const field of fields) {
        if (field === 'courseName') {
          setValue(UniStaffFormKeyNames.COURSE_NAME, '', { shouldValidate: false });
        } else if (field === 'mode') {
          setValue(UniStaffFormKeyNames.MODE, '', { shouldValidate: false });
        } else if (field === 'campus') {
          setValue(UniStaffFormKeyNames.CAMPUS, '', { shouldValidate: false });
        } else if (field === 'entryPoint') {
          setValue(UniStaffFormKeyNames.ENTRY_POINT, '', { shouldValidate: false });
        } else if (field === 'courseId') {
          setValue(UniStaffFormKeyNames.COURSE_ID, '', { shouldValidate: false });
        } else {
          setValue(field as keyof IUniStaffRegistrationForm, '', { shouldValidate: false });
        }
      }
    },
    [setValue],
  );

  const { universities: availableUniversities } = useUniStaffUniversities();

  return {
    form,
    uniOptions: availableUniversities,
    onSubmit: handleSubmit(onSubmit),
    clearDependentFields,
  };
}
